import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import { Link } from "react-router-dom";

import "swiper/css";

import { GetImageUrl } from "../../helpers/GetImageUrl";
import { http } from "../../axios/axios";

const OfferCard = ({ item }) => {
  return (
    <Link
      to={item.link}
      className="
        group relative block overflow-hidden rounded w-full
        aspect-[4/3]
        border border-white/20
        bg-slate-100
        shadow-lg
        hover:shadow-2xl
        transition-all duration-500
        cursor-pointer
      "
    >
      <img
        src={item.image}
        alt={item.altImageText || item.title}
        className="
          absolute inset-0
          w-full h-full
          object-cover
          transition-transform duration-700
          group-hover:scale-[1.05]
        "
      />

      <div
        className="
          absolute inset-0
          bg-gradient-to-t
          from-black/80
          via-black/30
          to-transparent
        "
      />

      <div
        className="
          absolute bottom-0 left-0 right-0
          p-4 sm:p-5 md:p-6 lg:px-4 lg:py-6
        "
      >
        <h2
          className="
            text-white
            font-bold
            mb-2
            text-lg
            sm:text-xl
            md:text-2xl
            2xl:text-3xl
            line-clamp-1
          "
        >
          {item.title}
        </h2>

        <p
          className="
            text-slate-200
            text-xs
            md:text-sm
            2xl:text-lg
            leading-relaxed
            line-clamp-2
          "
        >
          {item.desc}
        </p>

        <div
          className="
            mt-3
            text-xs
            font-semibold
            uppercase
            tracking-wider
            text-blue-300
            opacity-0
            translate-y-2
            transition-all
            duration-300
            group-hover:opacity-100
            group-hover:translate-y-0
          "
        >
          View Category →
        </div>
      </div>
    </Link>
  );
};

const activeStyle = "bg-blue-600 text-white shadow-md";

const normalStyle = "bg-slate-100 text-slate-700 hover:bg-slate-200";

 const buttonClass = (active) =>
  `
  rounded-md
  transition-all
  duration-200
  font-semibold

  min-w-[160px]
  text-center

  px-5
  py-2.5
  text-sm

  sm:min-w-[180px]
  sm:px-6
  sm:py-3
  sm:text-base

  lg:min-w-[190px]
  lg:px-7
  lg:py-3

  2xl:min-w-[210px]
  2xl:px-8
  2xl:py-3.5
  2xl:text-lg

  ${active ? activeStyle : normalStyle}
  `;

const WeOffer = () => {
  const [securityCategories, setSecurityCategories] = useState([]);
  const [itCategories, setItCategories] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    productType: "security",
    category: "",
    subCategory: "",
  });

  const selectProductType = (type) => {
    setSelectedFilters((prev) => ({
      ...prev,
      productType: type,
      category: "",
      subCategory: "",
    }));
  };

  useEffect(() => {
    let cancelled = false;

    Promise.allSettled([
      http.get("/category", { params: { limit: 8 } }),
      http.get("/ip-network/categories", { params: { limit: 8 } }),
    ]).then(([securityResult, itResult]) => {
      if (cancelled) return;

      if (securityResult.status === "rejected") {
        console.error("Security category fetch error:", securityResult.reason);
      }

      if (itResult.status === "rejected") {
        console.error("IT category fetch error:", itResult.reason);
      }

      setSecurityCategories(
        securityResult.status === "fulfilled"
          ? securityResult.value.data?.category || []
          : [],
      );

      setItCategories(
        itResult.status === "fulfilled"
          ? itResult.value.data?.categories || []
          : [],
      );
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const securityCards = securityCategories
    .map((item) => ({
      id: `security-${item._id}`,
      title: item.name,
      slug: item.slug,
      desc: item.description,
      image: GetImageUrl(item.categoryImage || item.image),
      altImageText: item.categoryImageAlt || item.imageAlt || item.name,
      link: `/security-products/category/${encodeURIComponent(item.slug)}`,
    }))
    .filter((item) => item.title && item.slug);

  const itCards = itCategories
    .map((item) => ({
      id: `it-${item._id}`,
      title: item.name,
      slug: item.slug,
      desc: item.description,
      image: GetImageUrl(item.categoryImage || item.image),
      altImageText: item.categoryImageAlt || item.imageAlt || item.name,
      link: `/it-products/category/${encodeURIComponent(item.slug)}`,
    }))
    .filter((item) => item.title && item.slug);

  const categories =
    selectedFilters.productType === "security" ? securityCards : itCards;
  const carouselCategories =
    categories.length === 4 ? [...categories, ...categories] : categories;

  return (
    <section
      className="
      py-10
      sm:py-8
      lg:py-8

      bg-white
      overflow-hidden
      "
    >
      <div
        className="
        px-4
        sm:px-6
        md:px-10
        lg:px-16

        mb-10
        text-center
        "
      >
        <span
          className="
          text-blue-600
          font-bold
          uppercase
          tracking-[0.2em]
          font-serif
          text-xs
          sm:text-sm
          "
        >
          Our Services
        </span>

        <h2
          className="
          mt-3
          font-black
          text-slate-900

          text-3xl
          sm:text-4xl
          md:text-5xl
          2xl:text-6xl
          font-serif
          "
        >
          What We Offer
        </h2>

        <div
          className="
          w-20
          lg:w-28
          h-1

          bg-blue-600

          mx-auto
          mt-4
          rounded-full
          "
        />

        <p
          className="
          max-w-4xl
          mx-auto

          mt-6

          text-slate-600

          text-sm
          sm:text-base
          2xl:text-lg
          font-serif
          leading-relaxed
          "
        >
          Suraksha 24/7 offers a comprehensive range of advanced security,
          surveillance and IT product solutions to meet the ever-changing requirements of
          different industries.
        </p>
      </div>

      <div className="relative flex justify-center flex-wrap gap-2 md:gap-3 mt-16 mb-12">
  <div
    className="
      absolute
      -top-12
      left-1/2
      -translate-x-1/2
      flex
      flex-col
      items-center
      text-blue-600
      font-semibold
      text-xs
      sm:text-sm
      pointer-events-none
    "
  >
    <span className="animate-pulse mb-1">
      Click here to choose
    </span>

    <span className="text-xl animate-bounce leading-none">
      ↓
    </span>
  </div>

  <button
    type="button"
    onClick={() => selectProductType("security")}
    className={buttonClass(
      selectedFilters.productType === "security"
    )}
  >
    Security Products
  </button>

  <button
    type="button"
    onClick={() => selectProductType("it")}
    className={buttonClass(
      selectedFilters.productType === "it"
    )}
  >
    IT Products
  </button>
</div>

      <Swiper
        key={`${selectedFilters.productType}-${categories.length}`}
        modules={[Autoplay]}
        loop={carouselCategories.length > 4}
        autoplay={{
          delay: 3500,
          disableOnInteraction: false,
        }}
        breakpoints={{
          0: {
            slidesPerView: 1,
            spaceBetween: 16,
          },

          480: {
            slidesPerView: 1,
            spaceBetween: 18,
          },

          640: {
            slidesPerView: 1,
            spaceBetween: 20,
          },

          768: {
            slidesPerView: 2,
            spaceBetween: 24,
          },

          1024: {
            slidesPerView: 3,
            spaceBetween: 26,
          },

          1440: {
            slidesPerView: 4,
            spaceBetween: 30,
          },

          2000: {
            slidesPerView: 4,
            spaceBetween: 35,
          },
        }}
        className="px-4 md:px-8 lg:px-16"
      >
        {carouselCategories.map((item, index) => (
          <SwiperSlide key={`${item.id}-${index}`} className="!h-auto">
            <OfferCard item={item} />
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
};

export default WeOffer;
